import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import useUsersData from '@/hooks/useUsersData';
import { useTranslation } from 'react-i18next';

export const useLeadTherapistFilter = (updateFilters) => {
    const { t } = useTranslation();
    const [selectedTherapistId, setSelectedTherapistId] = useState('');
    
    // Use the users data hook to get therapists dynamically
    const {
        data: users,
        loading: usersLoading,
        error: usersError,
        loadData: loadUsers
    } = useUsersData();

    // Load users data when the hook is initialized
    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleLoadTherapist = () => {
        // Show all users without filtering
        const availableTherapists = users;

        if (usersLoading) {
            Swal.fire({
                title: t('customers.list.userFilter.loadingTitle'),
                text: t('customers.list.userFilter.loadingText'),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });
            return;
        }

        if (usersError) {
            Swal.fire({
                title: t('customers.list.deleteDialog.errorTitle'),
                text: t('customers.list.userFilter.loadError', { error: usersError }),
                icon: 'error',
                confirmButtonText: t('customers.list.userFilter.ok')
            });
            return;
        }

        if (availableTherapists.length === 0) {
            Swal.fire({
                title: t('customers.list.userFilter.noUsersTitle'),
                text: t('customers.list.userFilter.noUsersText'),
                icon: 'info',
                confirmButtonText: t('customers.list.userFilter.ok')
            });
            return;
        }

        // Create HTML content for the SweetAlert with radio buttons using dynamic data
        const radioButtonsHtml = availableTherapists.map(user => `
            <div style="text-align: left; margin-bottom: 10px;">
                <label style="display: flex; align-items: center; cursor: pointer;">
                    <input type="radio" name="therapist" id="therapist-${user._id || user.id}" value="${user._id || user.id}" 
                           ${selectedTherapistId === (user._id || user.id) ? 'checked' : ''} 
                           style="margin-right: 8px;">
                    <span>${user.name || user.firstName + ' ' + user.lastName || user.email}</span>
                </label>
            </div>
        `).join('');

        Swal.fire({
            title: t('customers.list.userFilter.selectTitle'),
            html: `
                <div style="text-align: left;">
                    <div style="margin-bottom: 10px">${t('customers.list.userFilter.selectPrompt')}</div>
                    ${radioButtonsHtml}
                </div>
            `,
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('customers.list.userFilter.apply'),
            denyButtonText: t('customers.list.userFilter.clear'),
            cancelButtonText: t('customers.list.userFilter.cancel'),
            customClass: {
                denyButton: 'swal2-deny'
            },
            preConfirm: () => {
                const selectedRadio = document.querySelector('input[name="therapist"]:checked');
                return selectedRadio ? selectedRadio.value : '';
            }
        }).then((result) => {
            if (result.isDenied) {
                // Clear the filter
                setSelectedTherapistId('');
                updateFilters({ therapistId: null });
            } else if (result.isConfirmed) {
                const newSelectedTherapistId = result.value || '';
                
                setSelectedTherapistId(newSelectedTherapistId);
                
                // Apply the filter
                if (newSelectedTherapistId) {
                    updateFilters({ therapistId: newSelectedTherapistId });
                } else {
                    updateFilters({ therapistId: null });
                }
            }
        });
    };

    return {
        selectedTherapistId,
        handleLoadTherapist
    };
};
